import { Goto } from "@/shared/navigation/Goto";

import type { RobotoUriType } from "./magicLinks";

/**
 * The `roboto://` types whose page is reachable from the ID alone — a plain
 * string join, so they resolve inline in a chat message with no fetch and no
 * loading state.
 *
 * The four older types are absent because they need a lookup first: `topic`
 * and `msgpath` resolve the associated file (and a panel layout), `file`
 * branches on ingestion status, and `dataset` fetches for its tooltip. Those
 * keep the paths they had, in `RobotoUriRedirect` and `VizExternalLink`.
 */
const ENTITY_ROUTES = {
  collection: Goto.route.Collections,
  device: Goto.route.Devices,
  invocation: Goto.route.Invocations,
  org: Goto.route.Orgs,
  session: Goto.route.Sessions,
} satisfies Partial<Record<RobotoUriType, string>>;

/** A {@link RobotoUriType} that {@link robotoEntityPath} can resolve. */
export type DirectEntityType = keyof typeof ENTITY_ROUTES;

export function isDirectEntityType(
  type: RobotoUriType,
): type is DirectEntityType {
  return type in ENTITY_ROUTES;
}

/** Human-readable name for an entity type, for tooltips and error copy. */
export const ENTITY_TYPE_LABELS: Readonly<Record<DirectEntityType, string>> = {
  collection: "Collection",
  device: "Device",
  invocation: "Invocation",
  org: "Organization",
  session: "Session",
};

/**
 * In-app path for an ID-addressable entity, or `undefined` for a type this
 * cannot resolve.
 *
 * An empty `id` yields `undefined` rather than the collection *index* route:
 * a link the model wrote as a specific entity must not silently become a
 * navigation somewhere else.
 */
export function robotoEntityPath(
  type: RobotoUriType,
  id: string,
): string | undefined {
  if (!isDirectEntityType(type) || id === "") {
    return undefined;
  }
  return `${ENTITY_ROUTES[type]}/${encodeURIComponent(id)}`;
}
